import React from 'react';
import styles from './Testimonials.module.css';
import useInView from '../Animation/useInView.ts';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { Review } from '../../services/api/types.ts';
import { useResponsiveCarousel } from '../../hooks/useResponsiveCarousel.ts';

interface TestimonialsProps {
  title?: string;
  subtitle?: string;
  items: Review[];
  className?: string;
  onAddReviewClick?: () => void;
}

const Testimonials = ({
  title = "Отзывы наших клиентов",
  subtitle,
  items,
  className = "",
  onAddReviewClick
}: TestimonialsProps) => {
  const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const {
    canSlide,
    direction,
    isTransitioning,
    next,
    pause,
    prev,
    resume,
    visibleItems,
  } = useResponsiveCarousel(items, { autoPlayMs: 5000 });

  const gridClassName = [
    styles.grid,
    isTransitioning ? styles[direction] : '',
  ].filter(Boolean).join(' ');

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => {
      const star = i < rating ? <FaStar /> : <FaRegStar />;
      return (
        <span key={i} className={styles.star}>
          {star}
        </span>
      );
    });
  };

  return (
    <section
      id="TestimonialsId"
      ref={ref}
      className={`${styles.testimonials} ${className} ${isInView ? styles.visible : ''}`}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        <div
          className={gridClassName}
          onMouseEnter={canSlide ? pause : undefined}
          onMouseLeave={canSlide ? resume : undefined}
        >
          {visibleItems.map(({ item }, index) => (
            <div key={item.Id} className={styles.card}
              style={{ '--i': index } as React.CSSProperties}
              onClick={() => item.SourceUrl && window.open(item.SourceUrl, '_blank')}
            >
              <div className={styles.cardInner}>
                <div className={styles.rating}>{renderStars(item.Rating)}</div>
                <p className={styles.text}>"{item.Text}"</p>
                <div className={styles.authorSection}>
                  <div className={styles.avatar}>
                    <div className={styles.fallback}>
                      {item.Author.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className={styles.authorInfo}>
                    <span className={styles.authorName}>{item.Author}</span>
                    <span className={styles.source}>{item.Source}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {canSlide && (
          <div className={styles.controls}>
            <button className={styles.controlBtn} onClick={prev} disabled={isTransitioning} aria-label="Предыдущие отзывы">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className={styles.controlBtn} onClick={next} disabled={isTransitioning} aria-label="Следующие отзывы">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
        {onAddReviewClick && (
          <div className={styles.addButtonWrapper}>
            <button
              className={styles.addButton}
              onClick={onAddReviewClick}
              type="button"
            >
              Оставить отзыв
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
